<h1 mat-dialog-title>{{ fromPatientApp ? "From the patient's app" : 'Entry from another practice' }}</h1>

<div mat-dialog-content class="irv">
    <mat-progress-bar mode="indeterminate" *ngIf="isLoading"></mat-progress-bar>

    <div class="irv-alert irv-alert--error" *ngIf="loadError" data-test="load-error">
        <mat-icon>error_outline</mat-icon>
        <div>{{ loadError }}</div>
    </div>

    <ng-container *ngIf="entry">
        <div class="irv-origin" data-test="origin">
            <div class="irv-origin__icon"><mat-icon>{{ fromPatientApp ? 'phonelink_ring' : 'domain' }}</mat-icon></div>
            <div class="irv-origin__text">
                <div class="irv-origin__who">{{ fromPatientApp ? 'Shared by ' + entry.authorName + ' from the patient app' : entry.originGroupName }}</div>
                <div class="irv-origin__when">
                    <span *ngIf="data.patientName">{{ data.patientName }} · </span>
                    <span *ngIf="entry.authorName && !fromPatientApp">By {{ entry.authorName }} · </span>
                    <span *ngIf="entry.entryDate">dated {{ entry.entryDate | date: 'd MMM y' }} · </span>shared {{ entry.receivedAt | date: 'd MMM y, HH:mm' }}
                </div>
            </div>
            <span class="irv-kind" data-test="kind">{{ fromPatientApp ? 'Patient provided' : typeLabels[entry.sourceType] }}</span>
        </div>

        <div class="irv-note" data-test="rendered">
            <h3 class="irv-note__title">{{ entry.rendered.title }}</h3>
            <!-- A patient's entry is Markdown (results as a table), rendered as the timeline will show it. -->
            <div *ngIf="fromPatientApp" class="irv-note__md" [innerHtml]="entry.rendered.notes | formatMessage"></div>
            <p *ngIf="!fromPatientApp" class="irv-note__plain">{{ entry.rendered.notes }}</p>
        </div>

        <div class="irv-files" *ngIf="entry.files.length" data-test="files">
            <h4 class="irv-section__title">Files ({{ entry.files.length }})</h4>
            <ul class="irv-files__list">
                <li class="irv-file" *ngFor="let file of entry.files">
                    <mat-icon class="irv-file__icon" [class.irv-file__icon--failed]="file.status === 'failed'">
                        {{ file.status === 'stored' ? 'attach_file' : file.status === 'pending' ? 'hourglass_top' : 'warning_amber' }}</mat-icon>
                    <div class="irv-file__body">
                        <div>{{ file.role === 'page' ? 'Page ' + file.pageNumber : (file.fileName || 'File') }}</div>
                        <div class="irv-file__state" *ngIf="file.status === 'pending'">{{ fromPatientApp ? 'Still arriving' : 'Still being copied' }}</div>
                        <div class="irv-file__state" *ngIf="file.status === 'failed'">{{ fromPatientApp ? 'Could not be received' : 'Could not be copied' }}; it is not filed</div>
                    </div>
                    <button mat-stroked-button type="button" class="irv-file__open" *ngIf="fromPatientApp && file.status === 'stored' && file.role !== 'page'"
                            (click)="previewFile(file.id)" data-test="preview-file">Open</button>
                </li>
            </ul>
            <div class="irv-muted" *ngIf="entry.reviewStatus === 'pending'">The {{ fromPatientApp ? '' : 'copied ' }}files go to the patient's media with the note.</div>
        </div>

        <div class="irv-alert" *ngIf="entry.reviewStatus !== 'pending'" data-test="already-reviewed">
            <mat-icon>{{ entry.reviewStatus === 'validated' ? 'check_circle' : 'block' }}</mat-icon>
            <div>
                {{ entry.reviewStatus === 'validated' ? 'Validated to the timeline' : 'Dismissed' }}<span *ngIf="entry.reviewedAt"> on {{ entry.reviewedAt | date: 'd MMM y' }}</span>
                <div *ngIf="entry.dismissReason" class="irv-muted">Reason: {{ entry.dismissReason }}</div>
            </div>
        </div>

        <ng-container *ngIf="entry.canReview">
            <div class="irv-alert irv-alert--warning" *ngIf="warning" data-test="warning">
                <mat-icon>warning_amber</mat-icon>
                <div>{{ warning }}</div>
            </div>
            <p class="irv-intro" *ngIf="!fromPatientApp">Validating puts this entry on the patient's timeline as a read-only note, attributed to
                {{ entry.originGroupName }}. Dismissing keeps it out of the record.</p>
            <p class="irv-intro" *ngIf="fromPatientApp">Adding puts this on the patient's timeline as a read-only note tagged
                "Patient provided": it was not verified by the practice. The patient is told only that it was received.
                Dismissing keeps it out of the record, and the patient is not told why.</p>
            <div class="irv-option" *ngIf="alsoAdd && !dismissing">
                <mat-checkbox [(ngModel)]="alsoAddToList" data-test="also-add">{{ alsoAdd }}</mat-checkbox>
                <div class="irv-option__hint">Also files it as a row in that list, marked "Patient reported".</div>
            </div>
            <div class="irv-alert irv-alert--warning" *ngIf="blocker" data-test="blocker">
                <mat-icon>info</mat-icon>
                <div>{{ blocker }}</div>
            </div>
            <mat-form-field appearance="outline" class="irv-full" *ngIf="dismissing">
                <mat-label>Why is this entry being dismissed?</mat-label>
                <textarea matInput [(ngModel)]="dismissReason" [maxlength]="dismissMax" rows="3" cdkTextareaAutosize data-test="dismiss-reason"></textarea>
            </mat-form-field>
        </ng-container>

        <div class="irv-alert" *ngIf="!entry.canReview && entry.reviewStatus === 'pending'" data-test="cannot-review">
            <mat-icon>lock</mat-icon>
            <div>{{ entry.referralAccepted ? 'Only administrators and physicians can validate or dismiss entries.' : 'Accept the referral first: then its entries can be filed.' }}</div>
        </div>

        <div class="irv-alert irv-alert--error" *ngIf="actionError" data-test="action-error">
            <mat-icon>error_outline</mat-icon>
            <div>{{ actionError }}</div>
        </div>
    </ng-container>
</div>

<div mat-dialog-actions align="end">
    <button mat-button (click)="close()" [disabled]="isSaving">Close</button>
    <ng-container *ngIf="entry?.canReview">
        <ng-container *ngIf="!dismissing">
            <button mat-stroked-button color="warn" (click)="dismissing = true" [disabled]="isSaving" data-test="dismiss">Dismiss…</button>
            <button mat-flat-button color="primary" (click)="validate()" [disabled]="isSaving || !!blocker" data-test="validate">
                {{ isSaving ? 'Saving…' : 'Add to timeline' }}
            </button>
        </ng-container>
        <ng-container *ngIf="dismissing">
            <button mat-button (click)="dismissing = false; actionError = null" [disabled]="isSaving">Back</button>
            <button mat-flat-button color="warn" (click)="confirmDismiss()" [disabled]="isSaving || !dismissReason.trim()" data-test="confirm-dismiss">
                {{ isSaving ? 'Saving…' : 'Dismiss entry' }}
            </button>
        </ng-container>
    </ng-container>
</div>
